import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDownIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import UtmLink from '@/components/shared/UtmLink'

export function FAQ({
  faqs,
  className = '',
  title = 'FREQUENTLY ASKED QUESTIONS',
  includeStructuredData = true,
}) {
  const [openIndex, setOpenIndex] = useState(-1)

  if (!faqs || faqs.length === 0) {
    return null
  }

  const toggleItem = index => {
    setOpenIndex(openIndex === index ? -1 : index)
  }

  // Generate structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          faq.answerText ||
          (typeof faq.answer === 'string' ? faq.answer : faq.question),
      },
    })),
  }

  return (
    <section className={`w-full py-12 ${className}`}>
      {includeStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData),
          }}
        />
      )}

      <div className="mx-auto max-w-4xl px-4">
        <motion.h2
          className="text-foreground mb-8 text-center text-4xl font-black sm:mb-12 sm:text-5xl"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {title}
        </motion.h2>

        <div className="w-full space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="border-foreground bg-background rounded-lg border p-6 shadow-sm transition-all duration-300 hover:scale-105 hover:shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: 'easeOut',
              }}
            >
              <button
                onClick={() => toggleItem(index)}
                className="-m-2 flex w-full items-center justify-between rounded-md p-2 text-left focus:outline-none"
                aria-expanded={openIndex === index}
              >
                <span className="text-foreground text-xl font-bold">
                  {faq.question}
                </span>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <ChevronDownIcon className="text-muted-foreground h-5 w-5" />
                </motion.div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      duration: 0.4,
                      ease: 'easeInOut',
                      opacity: { duration: 0.3 },
                    }}
                    className="overflow-hidden"
                  >
                    <motion.div
                      className="pt-4"
                      initial={{ y: -10 }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      {typeof faq.answer === 'string' ? (
                        <div className="text-foreground leading-relaxed">
                          {faq.answer.split('\n\n').map((paragraph, idx) => {
                            // Check if this paragraph contains a list (ends with colon and has line breaks)
                            if (
                              paragraph.includes(':') &&
                              paragraph.includes('\n')
                            ) {
                              const parts = paragraph.split(':')
                              if (parts.length === 2) {
                                const [intro, listItems] = parts
                                const items = listItems
                                  .trim()
                                  .split('\n')
                                  .filter(item => item.trim())

                                return (
                                  <div
                                    key={idx}
                                    className={idx > 0 ? 'mt-4' : ''}
                                  >
                                    <p className="mb-4">{intro.trim()}:</p>
                                    <ul className="mb-4 list-inside list-disc space-y-1 pl-4">
                                      {items.map((item, itemIdx) => (
                                        <li key={itemIdx}>{item.trim()}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )
                              }
                            }

                            return (
                              <p key={idx} className={idx > 0 ? 'mt-4' : ''}>
                                {paragraph}
                              </p>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="text-foreground">{faq.answer}</div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button
            asChild
            className="h-14 cursor-pointer bg-[#ff914d] px-6 text-xl font-black text-white uppercase"
          >
            <UtmLink to="/faqs">View more claim FAQs</UtmLink>
          </Button>
        </div>
      </div>
    </section>
  )
}

export default FAQ
